import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { MessageCircle, Users, Flag, Trash2, Pencil, ChevronLeft, Search as SearchIcon } from "lucide-react";
import { toast } from "sonner";

const REACTIONS = [
  { key: "HELPFUL", label: "Helpful" },
  { key: "INSIGHTFUL", label: "Insightful" },
  { key: "SUPPORT", label: "Support" },
];
const REPORT_REASONS = [
  { v: "SPAM", l: "Spam" },
  { v: "HARASSMENT", l: "Harassment" },
  { v: "ACADEMIC_MISCONDUCT", l: "Academic misconduct" },
  { v: "INAPPROPRIATE_CONTENT", l: "Inappropriate content" },
  { v: "OTHER", l: "Other" },
];

function Reactions({ target, targetId, circleId, initial, onChange }) {
  const [state, setState] = useState(initial || { counts: {}, mine: [] });
  const react = async (rt) => {
    try {
      const r = await api.post(`/circles/${circleId}/reactions/${target}/${targetId}`, { reaction_type: rt });
      setState(r.data.reactions);
      onChange && onChange(r.data.reactions);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="flex gap-2 mt-3">
      {REACTIONS.map((r) => {
        const on = (state.mine || []).includes(r.key);
        const n = (state.counts || {})[r.key] || 0;
        return (
          <button
            key={r.key}
            onClick={() => react(r.key)}
            data-testid={`react-${r.key.toLowerCase()}-${targetId}`}
            className={`text-[11px] uppercase tracking-widest px-3 py-1 rounded-full border transition-colors ${
              on ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground"
            }`}
          >
            {r.label} {n > 0 && <span className="ml-1 opacity-80">{n}</span>}
          </button>
        );
      })}
    </div>
  );
}

function ReportModal({ open, onClose, contentType, contentId }) {
  const [reason, setReason] = useState("SPAM");
  const [desc, setDesc] = useState("");
  const [busy, setBusy] = useState(false);
  if (!open) return null;
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/moderation/report", { content_type: contentType, content_id: contentId, reason, description: desc });
      if (r.data.already_reported) toast.info("You already reported this — moderators are on it.");
      else toast.success("Report submitted. Thank you.");
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/40 p-6" data-testid="report-modal">
      <Card className="max-w-md w-full p-8">
        <h3 className="font-serif text-xl font-black tracking-tight">Report {contentType}</h3>
        <p className="text-xs text-muted-foreground mt-1">Your report is confidential. Moderators review each one.</p>
        <div className="mt-6 space-y-3">
          {REPORT_REASONS.map((r) => (
            <label key={r.v} className="flex items-center gap-2 text-sm">
              <input type="radio" name="reason" value={r.v} checked={reason === r.v} onChange={() => setReason(r.v)} data-testid={`report-reason-${r.v}`} />
              {r.l}
            </label>
          ))}
        </div>
        <Textarea value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Add context (optional, 1000 chars max)" maxLength={1000} className="mt-4" data-testid="report-desc" />
        <div className="mt-6 flex justify-end gap-3">
          <Button variant="outline" onClick={onClose} className="rounded-full" data-testid="report-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy} className="rounded-full" data-testid="report-submit">Submit report</Button>
        </div>
      </Card>
    </div>
  );
}

function CommentItem({ c, circleId, postId, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [body, setBody] = useState(c.body);
  const [reportOpen, setReportOpen] = useState(false);

  const save = async () => {
    try {
      await api.put(`/circles/${circleId}/posts/${postId}/comments/${c.id}`, { body });
      setEditing(false); onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async () => {
    if (!window.confirm("Remove this comment?")) return;
    try {
      await api.delete(`/circles/${circleId}/posts/${postId}/comments/${c.id}`);
      onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="border-l-2 border-border pl-4 py-3" data-testid={`comment-${c.id}`}>
      <p className="text-xs text-muted-foreground">
        <span className="text-foreground font-medium">{c.author_display_name}</span>
        {c.author_program && !c.is_anonymous && <> · {c.author_program}</>}
        <> · {new Date(c.created_at).toLocaleDateString()}</>
        {c.status === "EDITED" && <> · edited</>}
      </p>
      {editing ? (
        <div className="mt-2">
          <Textarea value={body} onChange={(e) => setBody(e.target.value)} maxLength={4000} className="min-h-[80px]" data-testid={`comment-edit-body-${c.id}`} />
          <div className="mt-2 flex gap-2">
            <Button size="sm" onClick={save} className="rounded-full" data-testid={`comment-save-${c.id}`}>Save</Button>
            <Button size="sm" variant="outline" onClick={() => { setEditing(false); setBody(c.body); }} className="rounded-full">Cancel</Button>
          </div>
        </div>
      ) : (
        <p className="mt-2 text-sm whitespace-pre-wrap leading-relaxed">{c.body}</p>
      )}
      <Reactions target="comment" targetId={c.id} circleId={circleId} initial={c.reactions} />
      <div className="mt-2 flex gap-3 text-[11px] text-muted-foreground">
        {c.can_edit && !editing && (
          <button onClick={() => setEditing(true)} data-testid={`comment-edit-${c.id}`} className="inline-flex items-center gap-1 hover:text-foreground"><Pencil className="h-3 w-3" /> Edit</button>
        )}
        {(c.can_edit || c.can_moderate) && (
          <button onClick={remove} data-testid={`comment-delete-${c.id}`} className="inline-flex items-center gap-1 hover:text-foreground"><Trash2 className="h-3 w-3" /> Remove</button>
        )}
        <button onClick={() => setReportOpen(true)} data-testid={`comment-report-${c.id}`} className="inline-flex items-center gap-1 hover:text-foreground"><Flag className="h-3 w-3" /> Report</button>
      </div>
      <ReportModal open={reportOpen} onClose={() => setReportOpen(false)} contentType="comment" contentId={c.id} />
    </div>
  );
}

function PostCard({ p, circleId, onOpen, isOwn, onDelete }) {
  const [reportOpen, setReportOpen] = useState(false);
  return (
    <Card className="p-6" data-testid={`post-card-${p.id}`}>
      <p className="text-xs text-muted-foreground">
        <span className="text-foreground font-medium">{p.author_display_name}</span>
        {p.author_program && !p.is_anonymous && <> · {p.author_program}</>}
        <> · {new Date(p.created_at).toLocaleDateString()}</>
        {p.status === "EDITED" && <> · edited</>}
      </p>
      {p.title && <h3 className="font-serif text-xl font-black tracking-tight mt-1">{p.title}</h3>}
      <p className="mt-3 text-[15px] whitespace-pre-wrap leading-relaxed line-clamp-6" data-testid={`post-body-${p.id}`}>{p.body}</p>
      <Reactions target="post" targetId={p.id} circleId={circleId} initial={p.reactions} />
      <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
        <button onClick={() => onOpen(p)} data-testid={`post-open-${p.id}`} className="inline-flex items-center gap-1 hover:text-foreground">
          <MessageCircle className="h-3.5 w-3.5" /> {p.comment_count || 0} comments · Open thread
        </button>
        <div className="flex gap-3">
          {isOwn && <button onClick={() => onDelete(p)} data-testid={`post-delete-${p.id}`} className="hover:text-foreground inline-flex items-center gap-1"><Trash2 className="h-3 w-3" /> Delete</button>}
          <button onClick={() => setReportOpen(true)} data-testid={`post-report-${p.id}`} className="hover:text-foreground inline-flex items-center gap-1"><Flag className="h-3 w-3" /> Report</button>
        </div>
      </div>
      <ReportModal open={reportOpen} onClose={() => setReportOpen(false)} contentType="post" contentId={p.id} />
    </Card>
  );
}

export default function CircleDetail() {
  const { cid } = useParams();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [c, setC] = useState(null);
  const [posts, setPosts] = useState([]);
  const [open, setOpen] = useState(null); // active thread
  const [comments, setComments] = useState([]);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [anon, setAnon] = useState(false);
  const [cbody, setCbody] = useState("");
  const [canon, setCanon] = useState(false);
  const [busy, setBusy] = useState(false);
  const currentUserIdRef = useRef(null);
  const [presence, setPresence] = useState(null);

  const load = useCallback(async () => {
    try {
      const [circleR, meR] = await Promise.all([
        api.get(`/circles/${cid}`),
        api.get("/auth/me"),
      ]);
      setC(circleR.data);
      currentUserIdRef.current = meR.data.id;
      if (circleR.data.is_member) {
        const p = await api.get(`/circles/${cid}/posts`);
        setPosts(p.data);
        // Presence chip — member-only, best-effort (never blocks the page).
        try {
          const pres = await api.get(`/circles/${cid}/presence`);
          setPresence(pres.data);
        } catch { /* presence is optional; fall through silently */ }
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }, [cid]);
  useEffect(() => { load(); }, [load]);

  // Deep-link (from Search "Open in Context"):
  //   ?post=<id>              → open that thread
  //   ?post=<id>&comment=<id> → open that thread and scroll to comment
  useEffect(() => {
    const targetPost = searchParams.get("post");
    const targetComment = searchParams.get("comment");
    if (!targetPost || !c?.is_member) return;
    const p = (posts || []).find((x) => x.id === targetPost);
    if (!p || (open && open.id === targetPost)) return;
    loadThread(p);
    if (targetComment) {
      const attempts = [220, 600, 1200, 2000];
      attempts.forEach((delay) => setTimeout(() => {
        const el = document.querySelector(`[data-testid="comment-${targetComment}"]`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          el.classList.add("ring-2", "ring-primary");
          setTimeout(() => el.classList.remove("ring-2", "ring-primary"), 2500);
        }
      }, delay));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, posts, c]);

  const loadThread = useCallback(async (post) => {
    setOpen(post);
    try {
      const r = await api.get(`/circles/${cid}/posts/${post.id}/comments`);
      setComments(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, [cid]);

  const join = async () => {
    try {
      await api.post(`/circles/${cid}/join`);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const leave = async () => {
    if (!window.confirm(`Leave ${c.name}?`)) return;
    try {
      await api.post(`/circles/${cid}/leave`);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const submitPost = async () => {
    if (body.trim().length < 5) return toast.error("Post is too short.");
    setBusy(true);
    try {
      await api.post(`/circles/${cid}/posts`, { title: title || null, body, is_anonymous: anon });
      setTitle(""); setBody(""); setAnon(false);
      toast.success("Post shared with the Circle.");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const submitComment = async () => {
    if (cbody.trim().length < 2) return;
    try {
      await api.post(`/circles/${cid}/posts/${open.id}/comments`, { body: cbody, is_anonymous: canon });
      setCbody(""); setCanon(false);
      await loadThread(open);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const deletePost = async (p) => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await api.delete(`/circles/${cid}/posts/${p.id}`);
      if (open?.id === p.id) setOpen(null);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!c) return <div className="p-10 text-muted-foreground">Loading…</div>;

  return (
    <div className="p-8 lg:p-12 max-w-4xl" data-testid="circle-detail-page">
      <Link to="/circles" className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground" data-testid="circle-back">
        <ChevronLeft className="h-3 w-3" /> All Circles
      </Link>
      <div className="mt-6 flex items-start gap-6 flex-wrap">
        <div className="flex-1 min-w-[240px]">
          <p className="text-xs uppercase tracking-[0.25em] text-primary">{c.category?.replace("_", " ")}</p>
          <h1 className="font-serif text-4xl lg:text-5xl font-black tracking-tight mt-2" data-testid="circle-name">{c.name}</h1>
          <p className="mt-4 text-muted-foreground max-w-2xl" data-testid="circle-description">{c.description}</p>
          <p className="mt-3 text-xs text-muted-foreground"><Users className="h-3 w-3 inline mr-1" /> <span data-testid="circle-member-count">{c.member_count}</span> {c.member_count === 1 ? "member" : "members"}</p>
          {c.is_member && presence && (presence.active_now > 0 || presence.active_recently > 0) && (
            <div className="mt-3 inline-flex items-center gap-2" data-testid="circle-presence-chip">
              {presence.active_now > 0 && (
                <span
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-500 px-2 py-0.5"
                  data-testid="circle-presence-active-now"
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  {presence.active_now} active now
                </span>
              )}
              {presence.active_recently > 0 && (
                <span
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest rounded-full border border-border bg-background/40 text-foreground/70 px-2 py-0.5"
                  data-testid="circle-presence-active-recently"
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-foreground/40" />
                  {presence.active_recently} active recently
                </span>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {c.is_member && (
            <Button
              variant="outline"
              onClick={() => nav(`/search?circle=${encodeURIComponent(cid)}`)}
              className="rounded-full"
              data-testid="circle-search-btn"
              title="Search this Circle"
            >
              <SearchIcon className="h-4 w-4 mr-1" /> Search
            </Button>
          )}
          {c.is_member ? (
            <Button variant="outline" onClick={leave} className="rounded-full" data-testid="circle-leave-btn">Leave Circle</Button>
          ) : (
            <Button onClick={join} className="rounded-full" data-testid="circle-join-btn">Join Circle</Button>
          )}
        </div>
      </div>

      <Card className="mt-8 p-4 lg:p-6 bg-secondary/40">
        <p className="text-[11px] uppercase tracking-widest text-muted-foreground">Circle rules</p>
        <p className="text-sm mt-2">Be a scholar. Cite ideas, not people. Assume good faith. Anonymous posts are welcome — misuse is not. Report anything that doesn't belong.</p>
      </Card>

      {!c.is_member ? (
        <Card className="mt-10 p-10 text-center" data-testid="circle-must-join">
          <p className="font-serif text-2xl font-black tracking-tight">Join to see the discussion.</p>
          <p className="text-sm text-muted-foreground mt-2">Only members read and post inside a Circle.</p>
        </Card>
      ) : (
        <>
          <Card className="mt-10 p-6" data-testid="new-post-card">
            <h2 className="font-serif text-lg font-black tracking-tight">Start a discussion</h2>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Optional title" maxLength={200} className="mt-3" data-testid="new-post-title" />
            <Textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Ask a question, share a resource, or start a discussion…" maxLength={8000} className="mt-3 min-h-[120px]" data-testid="new-post-body" />
            <div className="mt-3 flex items-center justify-between flex-wrap gap-3">
              <label className="flex items-center gap-2 text-sm text-muted-foreground">
                <Checkbox checked={anon} onCheckedChange={setAnon} data-testid="new-post-anon" />
                Ask anonymously
              </label>
              <Button onClick={submitPost} disabled={busy || body.trim().length < 5} className="rounded-full" data-testid="new-post-submit">Post</Button>
            </div>
          </Card>

          {posts.length === 0 ? (
            <Card className="mt-6 p-10 text-center" data-testid="posts-list-empty">
              <p className="font-serif text-2xl font-black tracking-tight">No discussions yet.</p>
              <p className="text-sm text-muted-foreground mt-2">Start the first conversation in this Circle.</p>
            </Card>
          ) : (
            <div className="mt-6 space-y-4" data-testid="posts-list">
              {posts.map((p) => (
                <PostCard
                  key={p.id}
                  p={p}
                  circleId={cid}
                  onOpen={loadThread}
                  isOwn={p.author_id === currentUserIdRef.current}
                  onDelete={deletePost}
                />
              ))}
            </div>
          )}
        </>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-foreground/40 p-0 sm:p-6" data-testid="post-thread-modal">
          <Card className="w-full sm:max-w-2xl max-h-[90vh] overflow-y-auto p-6 lg:p-8 rounded-t-2xl sm:rounded-2xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs text-muted-foreground">
                  <span className="text-foreground font-medium">{open.author_display_name}</span>
                  <> · {new Date(open.created_at).toLocaleDateString()}</>
                </p>
                {open.title && <h3 className="font-serif text-2xl font-black tracking-tight mt-2">{open.title}</h3>}
              </div>
              <button onClick={() => setOpen(null)} data-testid="thread-close" className="text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground">Close</button>
            </div>
            <p className="mt-4 text-[15px] whitespace-pre-wrap leading-relaxed">{open.body}</p>
            <Reactions target="post" targetId={open.id} circleId={cid} initial={open.reactions} />

            <p className="mt-8 text-xs uppercase tracking-widest text-muted-foreground">Comments ({comments.length})</p>
            <div className="mt-3 space-y-1" data-testid="comments-list">
              {comments.map((c) => (
                <CommentItem key={c.id} c={c} circleId={cid} postId={open.id} onChanged={() => loadThread(open)} />
              ))}
              {comments.length === 0 && <p className="text-sm text-muted-foreground">Be the first to reply.</p>}
            </div>

            <div className="mt-6 border-t border-border pt-6">
              <Textarea value={cbody} onChange={(e) => setCbody(e.target.value)} placeholder="Write a reply" maxLength={4000} className="min-h-[80px]" data-testid="new-comment-body" />
              <div className="mt-2 flex items-center justify-between gap-3">
                <label className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Checkbox checked={canon} onCheckedChange={setCanon} data-testid="new-comment-anon" />
                  Reply anonymously
                </label>
                <Button size="sm" onClick={submitComment} className="rounded-full" data-testid="new-comment-submit">Reply</Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
